import { NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Router } from "@angular/router";
import { Network } from "@capacitor/network";
import { AuthService } from "@core/auth.service";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { debounceTime, distinctUntilChanged, interval, Subscription } from "rxjs";
import { bounceIn } from "src/app/animations/custom.animation";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";

@Component({
  selector: "app-reset-password-popup",
  templateUrl: "./reset-password-popup.component.html",
  styleUrls: ["./reset-password-popup.component.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    NgClass,
    MatProgressSpinner,
    MatIcon,
    TranslatePipe,
  ],
})
export class ResetPasswordPopupComponent implements OnInit {
  passwordIcon = "eye-off";
  passwordType = "password";
  passwordTypeConfirm = "password";
  passwordIconConfirm = "eye-off";

  resetPasswordForm: FormGroup;
  spinnerAnimation: boolean;
  progressBar: number;
  timer$: any;
  sub: any;
  proceed = true;
  isOnline: boolean = true;
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  confirmPasswordSubscription: Subscription;
  constructor(
    private bottomSheet: MatBottomSheetRef<ResetPasswordPopupComponent>,
    private fb: FormBuilder,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    public translate: TranslateService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private GTMServise: GTMSagmentService,
    private GTMServiceSegment: GTMSegmentService,
    private route: ActivatedRoute,
    private ngZone: NgZone,
  ) {
    this.GTMServise?.GTMInitilization();
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l;
        translate.use(l ? l : "en");
      },
      (err) => {
        return err;
      }
    );

    this.authService.getConstantsMsg$?.subscribe((msg: any) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
  }

  async ngOnInit() {
    Network.addListener("networkStatusChange", (e) => {
      try {
        if (e.connected) {
          this.isOnline = true;
        } else {
          this.isOnline = false;
        }
      } catch (error) {
        return error;
      }
    });
    this.validateLoginForm();
    this.confirmPasswordSubscription = this.resetPasswordForm.controls.confirmPassword.valueChanges
      .pipe(
        debounceTime(300), // Wait for 300 milliseconds after the last value change
        distinctUntilChanged() // Only emit if the current value is different from the last
      )
      .subscribe(
        (data) => {
          if (!data) {
            this.resetPasswordForm.controls.confirmPassword.setErrors({ required: true });
            return;
          }
          if (this.resetPasswordForm.controls.password.value !== data) {
            this.resetPasswordForm.controls.confirmPassword.setErrors({ passwordMismatch: true });
          }
        },
        (err) => {
          return err;
        }
      );
  }

  moveHome() { }

  toggleViewPassword(): void {
    this.passwordType = this.passwordType === "text" ? "password" : "text";
    this.passwordIcon = this.passwordIcon === "eye" ? "eye-off" : "eye";
  }

  toggleViewPasswordConfirm() {
    this.passwordTypeConfirm = this.passwordTypeConfirm === "text" ? "password" : "text";
    this.passwordIconConfirm = this.passwordIconConfirm === "eye" ? "eye-off" : "eye";
  }

  private validateLoginForm() {
    this.resetPasswordForm = this.fb.group({
      password: ["", [Validators.required, Validators.minLength(6)]],
      confirmPassword: ["", [Validators.required, Validators.minLength(6), this.confirmPasswordValidation.bind(this)]],
    });
  }

  confirmPasswordValidation(control: FormControl) {
    if (control?.value) {
      if (this.resetPasswordForm.value.password !== control.value) {
        return { passwordMismatch: true };
      }
    }
    return null;
  }

  onPasswordResetSubmit() {
    if (this.resetPasswordForm.valid && window.navigator.onLine && this.data?.resetToken) {
      this.authService.resetPasswordOnFirebase(this.resetPasswordForm.value.password, this.data.msn, this.data.resetToken).then((d) => {
        this.authService.resetPassword(this.resetPasswordForm.value.password).subscribe(
          (res: any) => {
            if (res?.status === "success") {
              this.progressBar = 100;
              this.sub?.unsubscribe();
              setTimeout(() => {
                this.spinnerAnimation = false;
                this.proceed = false;
                this.changeDetectorRef.detectChanges();
                setTimeout(() => {
                  this.proceed = true;
                  this.bottomSheet.dismiss();
                  this.bottomSheet.afterDismissed().subscribe(() => {
                    this.ngZone.run(() => {
                      try {
                        console.info("Redirecting to next page after password reset");
                        // Query Params for Sells Journey
                        const queryParams = JSON.parse(JSON.stringify(this.route.snapshot.queryParams ?? {}));
                        const upsellId = queryParams?.upsellID ?? queryParams?.upsell_id ?? "";
                        const prosubID = queryParams?.prosub_id ?? localStorage.getItem("prosub_id");
                        const default_prosub_id = "2743";
                        const vc = queryParams?.vc ?? "";
                        const tp1 = queryParams?.TP1 ?? queryParams?.tp1 ?? "null";
                        const tp2 = queryParams?.TP2 ?? queryParams?.tp2 ?? "null";
                        const tp3 =
                          queryParams?.[Object.keys(queryParams ?? {}).find((key) => key?.toLowerCase() === "tp3")] ??
                          "null";
                        const t_id =
                          queryParams?.[
                          Object.keys(queryParams ?? {}).find((key) => key?.toLowerCase() === "t_id")
                          ] ?? "";
                        const ispartialReg = this.router.url.startsWith("/partial-registration");
                        const redirectTo = this.authService?.redirectTo ?? "/mainTabs/tabs/home";
                        // Check if it is partial registration and prosub_id is available
                        const prosub = this.GTMServise?.prosubDetails ?? this.GTMServiceSegment?.prosubDetails;
                        console.info('!isPartialReg:', !ispartialReg);
                        if (!ispartialReg) {
                          console.info('redirectTo:', redirectTo);
                          this.router.navigateByUrl(redirectTo);
                          return;
                        }
                        console.info("Redirection check:", prosubID, prosub?.optional_page, ispartialReg, prosubID && prosub?.optional_page && ispartialReg);
                        if (prosubID && prosub?.optional_page && ispartialReg) {
                          const defaultPage = "/account-created";
                          const defaultRoute = { optional_page: defaultPage };
                          const finalProsub = this.GTMServise?.prosubDetails || defaultRoute;
                          // Redirect to account created page
                          const queryParamObj = {
                            prosub_id: prosubID ?? default_prosub_id,
                            ...(this.GTMServise?.queryParamsToNavigate || {}),
                            vc,
                            TP1: tp1,
                            TP2: tp2,
                            TP3: tp3,
                            T_ID: t_id,
                            upsell_ID: upsellId,
                          };
                          console.info("Redirecting to:", finalProsub?.optional_page || defaultPage);
                          console.info("Query Params:", queryParamObj);
                          this.router
                            .navigate([finalProsub?.optional_page || defaultPage], {
                              queryParams: {
                                prosub_id: prosubID ?? default_prosub_id,
                                ...(this.GTMServise?.queryParamsToNavigate || {}),
                                vc,
                                TP1: tp1,
                                TP2: tp2,
                                TP3: tp3,
                                T_ID: t_id,
                                upsell_ID: upsellId,
                              },
                            })
                            .then((success) => {
                              if (!success) {
                                console.error("Navigation failed");
                              }
                            });
                        }
                      } catch (error) {
                        console.info("error in resetPassword redirection:", error);
                        return error;
                      }
                    });
                  });
                }, 2000);
              }, 300);
            } else {
              this.spinnerAnimation = false;
              this.proceed = false;
              this.bottomSheet.dismiss();
              this.authService
                .getConstantsMsg("1026")
                .then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "We couldn't reset your password. Try again.",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: "Error",
                    },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                })
                .catch((error) => {
                  return error;
                });
            }
          },
          (err) => {
            return err;
          }
        );
      })
        .catch((error) => {
          return error;
        });
      // });
    }

    // if (this.updateForm.valid && window.navigator.onLine) {
    //   this.authService.resetPasswordOnFirebase(this.updateForm.value.newPassword, this.data.msn).then((d) => {
    //     this.authService.updatePassword(this.updateForm.value).subscribe(
    //       (res: any) => {
    //         this.spinnerAnimation = false;
    //         this.progressBar = 100;
    //         this.sub?.unsubscribe();
    //         this.changeDetectorRef.detectChanges();
    //         if (res.status === "success") {
    //           this.proceed = true;
    //           // this.authService.resetPasswordOnFirebase(this.updateForm.value.newPassword, this.data.msn);
    //           this.backToDetails();
    //         } else {
    //           this.snackService.openSnackBar(res.message, "", "Error", "Error");
    //           this.proceed = true;
    //           this.apiFailed = false;
    //           this.changeDetectorRef.detectChanges();
    //         }
    //       },
    //       (err) => {
    //         return err;
    //       }
    //     );
    //   });
    // }
  }

  dismissDialog() {
    this.bottomSheet.dismiss();
  }

  startSpinner() {
    this.spinnerAnimation = true;
    this.progressBar = 0;
    this.timer$ = interval(20);
    this.sub = this.timer$?.subscribe(
      (sec) => {
        this.progressBar = 0 + sec;
        this.changeDetectorRef.detectChanges();
      },
      (err) => {
        return err;
      }
    );
  }

  ngOnDestroy() {
    if (this.confirmPasswordSubscription) {
      this.confirmPasswordSubscription?.unsubscribe();
    }
  }
}
